import React from 'react';
import { Pressable, View } from 'react-native';

import { ThemedText } from '@/components/ThemedText';
import AppStyles from '@/constants/Styles';
import { useThemeColor } from '@/hooks/useThemeColor';
import { LeafletingRouteAssignment } from '@/types/leafleting';

interface LeafletingAssignmentItemProps {
  assignment: LeafletingRouteAssignment;
  onPress: (assignment: LeafletingRouteAssignment) => void;
}

export default function LeafletingAssignmentItem({ assignment, onPress }: LeafletingAssignmentItemProps) {
  // Theme-aware colors
  const cardBackgroundColor = useThemeColor({}, 'cardBackground');
  const borderColor = useThemeColor({}, 'border');
  const secondaryTextColor = useThemeColor({}, 'secondaryText');
  const progressHigh = useThemeColor({}, 'progressHigh');
  const progressMedium = useThemeColor({}, 'progressMedium');
  const progressLow = useThemeColor({}, 'progressLow');
  const progressAssigned = useThemeColor({}, 'progressAssigned');

  // Get status-based color for progress badge
  const getProgressColor = (status: string): string => {
    switch (status) {
      case 'completed':
        return progressHigh; // Green
      case 'in_progress':
        return progressMedium; // Orange
      case 'assigned':
        return progressAssigned; // Red
      case 'unassigned':
      case 'not_doing':
      default:
        return progressLow; // Dark grey
    }
  };

  // Calculate progress: 100% if completed, 0% otherwise
  const isCompleted = assignment.status === 'completed';
  const progressPercentage = isCompleted ? 100 : 0;
  const progressColor = getProgressColor(assignment.status);

  return (
    <Pressable
      style={[
        AppStyles.engagementItem,
        { 
          backgroundColor: cardBackgroundColor, 
          borderWidth: 1, 
          borderColor 
        }
      ]}
      onPress={() => onPress(assignment)}
    >
      <View style={{ width: '100%' }}>
        {/* Header Row: Round name and route name on left, progress box on right */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <View style={{ flex: 1, marginRight: 8 }}>
            <ThemedText style={AppStyles.headerText}>
              {assignment.round_name}
            </ThemedText>
            <ThemedText style={{ fontSize: 14, color: secondaryTextColor, marginTop: 2 }}>
              {assignment.number_within_branch}: {assignment.route_name || `Route Assignment ${assignment.assignment_id.slice(-8)}`}
            </ThemedText>
          </View>
          <View style={{ 
            backgroundColor: progressColor,
            paddingHorizontal: 12,
            paddingVertical: 6,
            borderRadius: 16,
            minWidth: 60,
            alignItems: 'center'
          }}>
            <ThemedText style={{ color: '#ffffff', fontSize: 14, fontWeight: 'bold' }}>
              {progressPercentage}%
            </ThemedText>
          </View>
        </View>
        
        {/* Address count - left aligned */}
        <ThemedText style={{ fontSize: 12, color: secondaryTextColor, marginTop: 4 }}>
          {assignment.address_count} addresses
        </ThemedText>
        
        {assignment.date_started && (
          <ThemedText style={{ color: secondaryTextColor, fontSize: 12, marginTop: 2 }}>
            Started: {new Date(assignment.date_started).toLocaleDateString()}
          </ThemedText>
        )}
      </View>
    </Pressable>
  );
}
